<template>
  <div class="input-group flex-nowrap item-summary-view" :style="stylePosition">
    <span
        v-if="position && !isHandRaise"
        class="input-group-text bg-white p-0"
        style="display: grid; grid-template-areas: 'pos up' 'pos down'; grid-template-columns: 3rem auto;"
    >
      <input
          type="number"
          class="item-position-input"
          style="grid-area: pos; place-self: stretch; text-align: center"
          :value="position"
          :disabled="locked"
          min="1"
          :max="selectedQty"
          @change="changePosition"
          @keydown.enter.prevent="changePosition"
      >
      <button
        type="button"
        class="btn btn-light btn-sm rounded-0"
        :disabled="isAtTop || locked"
        :aria-label="$t('backend.actions.move_up')"
        @click="moveUp"
      >
        <AVIcon icon="chevron-up" />
      </button>
      <button
        type="button"
        class="btn btn-light btn-sm rounded-0"
        :disabled="isAtBottom || locked"
        :aria-label="$t('backend.actions.move_down')"
        @click="moveDown"
      >
        <AVIcon icon="chevron-down" />
      </button>
    </span>
    <button
        type="button"
        style="min-width: 0"
        class="btn flex-grow-1 hstack justify-content-between"
        :class="{
          'btn-primary': isSelected,
          'btn-secondary': !isSelected,
          'btn-info': item.itemType === 'InformationPage' && isSelected,
          disabled: (disabled && !isSelected) || locked
        }"
        @click="handleItem"
      >
      <span class="text-truncate"
            :class="{'text-light': isSelected}"
            v-text="item.label"
      ></span>
      <span class="d-flex flex-column align-items-center flex-shrink-1" style="min-width: 0">
        <span v-if="item.itemType === 'Contest'" class="badge" :class="{'text-bg-light': isSelected, 'text-bg-primary': !isSelected}">
          {{ $t("js.backend.voting_round_modal.option_counter", { n: item.optionsCount, plural: item.optionsCount }) }}
        </span>
        <span v-else class="badge" :class="{'text-bg-light': isSelected, 'text-bg-info': !isSelected}">
          <AVIcon icon="info-circle" class="me-1" />{{ $t("js.backend.item_summary_view.information_page") }}
        </span>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { PropType, Locale } from "@/types";
import type { SelectableItem } from "@/components/backend/ItemSelectionList.vue";

const props = defineProps({
  item: {
    type: Object as PropType<SelectableItem>,
    required: true,
  },
  isSelected: {
    type: Boolean,
    required: true,
  },
  editMode: {
    type: Boolean,
    required: true,
  },
  position: {
    type: Number,
    required: true,
  },
  selectedQty: {
    type: Number,
    required: true,
  },
  orderError: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isHandRaise: {
    type: Boolean,
    default: false,
  },
  locked: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["handleItem", "handleItemPosition"]);

const stylePosition = computed(() => isNaN(props.position)
  ? { "grid-column": props.isSelected ? 2 : 1 }
  : { "grid-row": props.position, "grid-column": props.isSelected ? 2 : 1 }
);

const isAtTop = computed(() => props.position === 1);

const isAtBottom = computed(() => props.position === props.selectedQty);

const handleItem = () => {
  if (!props.isSelected && props.disabled) return;
  if (!props.editMode) emits("handleItem", props.item.reference, props.item.itemType, props.position);
};

const moveUp = () => {
  if (isAtTop.value || props.editMode) return;
  emits("handleItemPosition", props.item.reference, props.item.itemType, props.position - 1);
};

const moveDown = () => {
  if (isAtBottom.value || props.editMode) return;
  emits("handleItemPosition", props.item.reference, props.item.itemType, props.position + 1);
};

const changePosition = (event: Event) => {
  if (props.editMode) return;

  const input = event.target as HTMLInputElement;
  const nextPosition = Math.min(Math.max(1, Number(input.value)), props.selectedQty);
  input.value = nextPosition.toString();
  emits("handleItemPosition", props.item.reference, props.item.itemType, nextPosition);
};

</script>

<style lang="css" scoped>
.item-position-input {
  border: none;
  background: none;
  -moz-appearance: textfield;
}

.item-position-input::-webkit-inner-spin-button,
.item-position-input::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.item-summary-view {
  opacity: 1.0;
  min-width: 100%;
}

.disabled {
  opacity: 0.7;
}
</style>

